import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  Alert,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardMedia
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { collection, getDocs, doc, setDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../../firebase';

const GOLD = '#d4af37';

interface NewsArticle {
  id: string;
  title: string;
  content: string;
  imageUrl: string;
  date: string;
  author: string;
}

export default function NewsArticlesManager() {
  const [articles, setArticles] = useState<NewsArticle[]>([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState<NewsArticle>({
    id: '',
    title: '',
    content: '',
    imageUrl: '',
    date: new Date().toISOString().split('T')[0],
    author: ''
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [imagePreview, setImagePreview] = useState<string>('');

  useEffect(() => {
    loadArticles();
  }, []);

  const loadArticles = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, 'news_section_articles'));
      const data: NewsArticle[] = [];
      querySnapshot.forEach((doc) => {
        data.push({ id: doc.id, ...doc.data() } as NewsArticle);
      });
      data.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
      setArticles(data);
      console.log('✅ Loaded news articles:', data.length);
    } catch (error) {
      console.error('Error loading articles:', error);
      setSnackbar({ open: true, message: 'Error loading articles', severity: 'error' });
    }
  };

  const handleSave = async () => {
    if (!formData.title.trim() || !formData.content.trim() || !formData.date) {
      setSnackbar({ open: true, message: 'Please fill all required fields', severity: 'error' });
      return;
    }

    try {
      const docId = editingId || `news_${Date.now()}`;
      const docRef = doc(db, 'news_section_articles', docId);
      await setDoc(docRef, {
        title: formData.title,
        content: formData.content,
        imageUrl: formData.imageUrl,
        date: formData.date,
        author: formData.author
      });
      
      setSnackbar({ 
        open: true, 
        message: editingId ? 'Article updated successfully!' : 'Article added successfully!', 
        severity: 'success' 
      });
      
      resetForm();
      loadArticles();
    } catch (error) {
      console.error('Error saving article:', error);
      setSnackbar({ open: true, message: 'Error saving article', severity: 'error' });
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this article?')) {
      try {
        await deleteDoc(doc(db, 'news_section_articles', id));
        setSnackbar({ open: true, message: 'Article deleted successfully!', severity: 'success' });
        loadArticles();
      } catch (error) {
        console.error('Error deleting article:', error);
        setSnackbar({ open: true, message: 'Error deleting article', severity: 'error' });
      }
    }
  };

  const handleEdit = (article: NewsArticle) => {
    setFormData(article);
    setEditingId(article.id);
    setImagePreview(article.imageUrl);
    setOpenDialog(true);
  };

  const resetForm = () => {
    setFormData({
      id: '',
      title: '',
      content: '',
      imageUrl: '',
      date: new Date().toISOString().split('T')[0],
      author: ''
    });
    setEditingId(null);
    setImagePreview('');
    setOpenDialog(false);
  };

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        setSnackbar({ open: true, message: 'Please select a valid image file', severity: 'error' });
        return;
      }
      if (file.size > 2 * 1024 * 1024) {
        setSnackbar({ open: true, message: 'Image size must be less than 2MB', severity: 'error' });
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result as string;
        setFormData({ ...formData, imageUrl: base64String });
        setImagePreview(base64String);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setFormData({ ...formData, imageUrl: '' });
    setImagePreview('');
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: GOLD }}>
          📰 News Articles Manager
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => {
            resetForm();
            setOpenDialog(true);
          }}
          sx={{ bgcolor: GOLD, color: '#000', fontWeight: 700, '&:hover': { bgcolor: '#e5c158' } }}
        >
          Add Article
        </Button>
      </Box>

      <TableContainer component={Paper} sx={{ border: `2px solid ${GOLD}` }}>
        <Table>
          <TableHead sx={{ bgcolor: GOLD }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 700, color: '#000' }}>Title</TableCell>
              <TableCell sx={{ fontWeight: 700, color: '#000' }}>Author</TableCell>
              <TableCell sx={{ fontWeight: 700, color: '#000' }}>Date</TableCell>
              <TableCell sx={{ fontWeight: 700, color: '#000' }}>Preview</TableCell>
              <TableCell align="center" sx={{ fontWeight: 700, color: '#000' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {articles.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">No articles yet. Add your first news article!</Typography>
                </TableCell>
              </TableRow>
            ) : (
              articles.map((article) => (
                <TableRow key={article.id} hover>
                  <TableCell sx={{ fontWeight: 600 }}>{article.title}</TableCell>
                  <TableCell>{article.author || 'N/A'}</TableCell>
                  <TableCell>{article.date}</TableCell>
                  <TableCell sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {article.content.substring(0, 60)}...
                  </TableCell>
                  <TableCell align="center">
                    <IconButton size="small" onClick={() => handleEdit(article)} sx={{ color: GOLD }}>
                      <EditIcon />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleDelete(article.id)} sx={{ color: '#e53935' }}>
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={openDialog} onClose={resetForm} maxWidth="md" fullWidth>
        <DialogTitle sx={{ bgcolor: GOLD, color: '#000', fontWeight: 700 }}>
          {editingId ? 'Edit Article' : 'Add New Article'}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <TextField
            fullWidth
            label="Title"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Author"
            value={formData.author}
            onChange={(e) => setFormData({ ...formData, author: e.target.value })}
            margin="normal"
          />
          <TextField
            fullWidth
            label="Date"
            type="date"
            value={formData.date}
            onChange={(e) => setFormData({ ...formData, date: e.target.value })}
            margin="normal"
            InputLabelProps={{ shrink: true }}
            required
          />
          <Box sx={{ my: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Article Image</Typography>
            <Button
              component="label"
              variant="outlined"
              startIcon={<CloudUploadIcon />}
              sx={{ mb: 2, borderColor: GOLD, color: GOLD, '&:hover': { borderColor: '#e5c158', bgcolor: 'rgba(212,175,55,0.05)' } }}
            >
              Upload Image (Base64)
              <input
                type="file"
                hidden
                accept="image/*"
                onChange={handleImageUpload}
              />
            </Button>
            <Typography variant="caption" display="block" color="text.secondary" sx={{ mb: 1 }}>
              Supported formats: JPG, PNG, GIF. Max size: 2MB
            </Typography>
            
            {(imagePreview || formData.imageUrl) && (
              <Card sx={{ maxWidth: 400, mb: 2 }}>
                <CardMedia
                  component="img"
                  height="200"
                  image={imagePreview || formData.imageUrl}
                  alt="Preview"
                  sx={{ objectFit: 'cover' }}
                />
                <Box sx={{ p: 1, textAlign: 'center' }}>
                  <Button size="small" color="error" onClick={handleRemoveImage}>
                    Remove Image
                  </Button>
                </Box>
              </Card>
            )}
            
            <TextField
              fullWidth
              label="Or paste Image URL"
              value={formData.imageUrl && !formData.imageUrl.startsWith('data:') ? formData.imageUrl : ''}
              onChange={(e) => {
                setFormData({ ...formData, imageUrl: e.target.value });
                setImagePreview(e.target.value);
              }}
              size="small"
              placeholder="https://example.com/image.jpg"
            />
          </Box>
          <TextField
            fullWidth
            label="Content"
            value={formData.content}
            onChange={(e) => setFormData({ ...formData, content: e.target.value })}
            margin="normal"
            multiline
            rows={6}
            required
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={resetForm}>Cancel</Button>
          <Button 
            onClick={handleSave} 
            variant="contained" 
            sx={{ bgcolor: GOLD, color: '#000', fontWeight: 700, '&:hover': { bgcolor: '#e5c158' } }}
          >
            {editingId ? 'Update' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={4000} 
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
